import React, { useState, useEffect, Fragment, useMemo, useRef } from 'react';
import theme from "@modul-connect/shared/theme";
import View from "@modul-connect/shared/components/atoms/view";
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import { TableSortLabel } from '@mui/material';
import { useLoadingTimer } from '@modul-connect/shared/hooks';
import { H3, H5, P } from '../../atoms/text';
import Button from '../../atoms/button'
import { MdDelete, MdEdit, MdCheck, MdClose, MdFileDownload } from "react-icons/md";
import Tag from '../../atoms/tags/tag';
import { BubbleLoadingWidget } from '../bubbleLoadingWidget/bubbleLoadingWIdget';

const getCellValue = (row, column) => {
  if (column.hasGuard) {
    return (row[column.id]?.value || row[column.id]?.value === 0) ? row[column.id].value : null;
  }
  return (row[column.id] || row[column.id] === 0) ? row[column.id] : null;
};

const TableWidget = ({
  data = [],
  totalItems,
  columns,
  onFetchData,
  onClickRow,
  searchStr = '',
  themes,
  extend,
  error,
  selectedRows,
  onRemove,
  editableIds,
  removableIds,
  onEdit,
  onDownload,
  customRowButton,
  rowsOptions,
  defaultRowsPerPage,
  tag,
  defaultSort,
  isSortable = true,
  pagination = true,
  fetchAgainOnChange,
  sidescroll,
  loadingStatus,
  hide,
  isSaving,
}) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(defaultRowsPerPage ?? 10);
  
  const initialSortId = defaultSort ? defaultSort.replace('desc', '') : 'created_date';
  const initialSortDir = defaultSort && defaultSort.endsWith('desc') ? 'desc' : 'asc';
  
  const [sortId, setSortId] = useState(initialSortId);
  const [sortOrder, setSortOrder] = useState(initialSortDir);
  
  const [showTableWhileLoading, setShowTableWhileLoading] = useState(false);
  
  const isLoading = loadingStatus === 'loading';
  const loadingFailed = loadingStatus === 'failed';

  useLoadingTimer(() => setShowTableWhileLoading(false));

  useEffect(() => {
    if (!onFetchData) return;
    onFetchData();
  }, []); 

  useEffect(() => {
    setPage(0);
  }, [searchStr, tag, rowsPerPage]);

  useEffect(() => {
      setRowsPerPage(defaultRowsPerPage ?? 10)
  }, [rowsOptions]);

  const processedData = useMemo(() => {
    if (!data) return [];

    let result = [...data];

    if (searchStr) {
      const query = searchStr.toLowerCase();
      result = result.filter(row => {

        if(row.isDivider) return false; 
        
        return columns.some(col => {

          const val = getCellValue(row, col);
          return val ? String(val).toLowerCase().includes(query) : false;
        });
      });
    }


    if (tag) {

       result = result.filter(row => {
         if(row.isDivider) return false;
         
         const tagColumns = columns.filter(c => c.isTag);
         const matchesCol = tagColumns.some(col => {
            const val = getCellValue(row, col);
            return val === tag;
         });

         return matchesCol;
       });
    }

    if (sortId) {
      result.sort((a, b) => {

        if(a.isDivider) return -1;
        if(b.isDivider) return 1;

        const valA = getCellValue(a, { id: sortId, hasGuard: columns.find(c=>c.id === sortId)?.hasGuard });
        const valB = getCellValue(b, { id: sortId, hasGuard: columns.find(c=>c.id === sortId)?.hasGuard });

        if (valA === valB) return 0;
        if (valA === null || valA === undefined) return 1;
        if (valB === null || valB === undefined) return -1;

        if (typeof valA === 'number' && typeof valB === 'number') {
           return sortOrder === 'asc' ? valA - valB : valB - valA;
        }

        const strA = String(valA).toLowerCase();
        const strB = String(valB).toLowerCase();

        if (strA < strB) return sortOrder === 'asc' ? -1 : 1;
        if (strA > strB) return sortOrder === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return result;
  }, [data, searchStr, tag, sortId, sortOrder, columns]);

  const visibleRows = useMemo(() => {
    return processedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  }, [processedData, page, rowsPerPage]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = event => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const handleSortBy = (columnId) => {
    const isAsc = sortId === columnId && sortOrder === 'asc';
    setSortOrder(isAsc ? 'desc' : 'asc');
    setSortId(columnId);
    setPage(0);
  }

  let columns_filtered;
  switch (themes?.device) {
    case 'tablet':
      columns_filtered = columns.filter(x => x.hide?.indexOf('tablet') === -1);
      break;
    case 'mobile':
      columns_filtered = columns.filter(x => x.hide?.indexOf('mobile') === -1);
      break;
    default:
      columns_filtered = columns;
  }

  const noData = !processedData || processedData.length === 0;

  return (
    hide ? null :
    <View extend={[extend, error && styles.error, sidescroll && {overflowY: 'scroll'} ]}>
      { isSaving && <P>Saving ...</P>}
      { (!isLoading || showTableWhileLoading) && !noData ? (
        <Fragment>
          <Table aria-label="sticky table">
            <TableHead>
              <TableRow>
                {columns_filtered.map((column) => (
                  <TableCell
                    key={column.id}
                    align={column.align}
                    style={ { fontFamily: 'futura', maxWidth: column.maxWidth, minWidth: column.minWidth, width: column.width  }}
                    className={ 'mc-table-head' }
                    onClick= {() => {
                      if (isSortable) {
                        handleSortBy(column.id)
                      }
                    }}
                  >
                    {column.label}
                    {isSortable ?
                      <TableSortLabel
                        active={sortId === column.id}
                        direction={sortId === column.id ? sortOrder : 'asc'}
                      >
                      </TableSortLabel> : null
                    }
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleRows.map((row, index) => {
                if (row.isDivider) {
                  return (
                    <TableRow tabIndex={-1} key={row.id + "_" + index}>
                      <TableCell
                        style={ getDividerClass(index) }
                        className={ 'mc-table-divider' }
                        key={row.id}
                        colSpan={columns_filtered.length + (onEdit || onRemove || onDownload || customRowButton ? 1 : 0)}
                      >
                        <H5>{ row.name.toUpperCase() }</H5>
                      </TableCell>
                    </TableRow>
                  )
                }
                return (
                  <TableRow
                    style={ (selectedRows && selectedRows.includes(row.id)) ? styles.selected : null }
                    hover
                    role="checkbox"
                    tabIndex={-1}
                    key={row.id + "_" + index}
                  >
                    {
                      columns_filtered.map(column => {
                        let value, guard;
                        
                        if (column.hasGuard) {
                          value =  (row[column.id]?.value || row[column.id]?.value === 0) ? row[column.id].value : '-';
                          guard = (row[column.id]?.guard || row[column.id]?.value === 0) ? row[column.id].guard : null;
                        }
                        else {
                          value = (row[column.id] || row[column.id] === 0) ? row[column.id] : '-';
                        }
                        
                        return (
                          <TableCell
                            key={column.id}
                            align={column.align}
                            onClick={event => {
                              const wasClickOnBtn = event.target.tagName === 'BUTTON' || event.target.tagName === 'IMG'
                              if (onClickRow && !wasClickOnBtn) {
                                onClickRow(event, row.id, row)
                              }
                            }}
                          >
                            <View extend={{display: 'flex'}}>
                              {
                                column.isTag ?
                                  <Tag textColor={column.textColor} value={value} tagColor={ column.tagColor ? column.tagColor(value, guard) : null} />
                                : column.isCheck && value === true ?
                                  <MdCheck/>
                                : column.isCheck ?
                                  <MdClose/>
                                : column.format ? column.format(value, row) : value
                              }
                              { column.showButton && column.showButton(value) &&
                                <Button noBg noPadding smaller
                                  extend={{marginLeft: '10px', '&:hover': { textDecoration: 'underline' }}}
                                  disabled={column.buttonDisabled ? column.buttonDisabled(row) : false}
                                  onClick={ () => column.onClickButton && column.onClickButton(row) }>
                                  {typeof column.buttonTitle === 'function' ? column.buttonTitle(row) : column.buttonTitle  }
                                </Button>
                              }
                            </View>
                          </TableCell>
                        );
                      })
                    }
                    {
                      (onEdit || onRemove || onDownload || customRowButton) && (
                        <TableCell
                          key={ 'button' }
                          align={ 'right' }
                          style={{
                            ...styles.action,
                            ...(customRowButton && { width: 'auto', minWidth: 120 }),
                          }}
                        >
                        {
                          onEdit &&
                          (editableIds ? (editableIds.includes(row.id)) : true) && (
                              <MdEdit onClick={ (event) => onEdit(event, row.id) } />
                          )
                        }
                        {
                          onRemove &&
                          (removableIds ? (removableIds.includes(row.id)) : true) && (
                              <MdDelete style={{marginLeft: 10}} onClick={ (event) => onRemove(event, row.id) } />
                          )
                        }
                        {
                          onDownload &&
                          (removableIds ? (removableIds.includes(row.id)) : true) && (
                              <MdFileDownload style={{marginLeft: 10}} onClick={ (event) => onDownload(event, row.id) } />
                          )
                        }
                        {
                          customRowButton &&
                          customRowButton.visible(row) && (
                              <Button
                                noBg
                                noPadding
                                smaller
                                extend={{ marginLeft: 10 }}
                                onClick={(event) => {
                                  event.stopPropagation();
                                  customRowButton.onClick(event, row.id, row);
                                }}
                              >
                                {typeof customRowButton.label === 'function'
                                  ? customRowButton.label(row)
                                  : customRowButton.label}
                              </Button>
                          )
                        }
                        </TableCell>
                      ) 
                    }
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          {pagination &&
            <TablePagination
              showFirstButton
              showLastButton
              rowsPerPageOptions={rowsOptions || [10, 25, 50, 100]}
              component="div"
              count={ processedData.length } 
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />}
        </Fragment>
      ) : (
        <Fragment>
          {
            isLoading ? <BubbleLoadingWidget text={ "Loading ... " }/>
            : loadingFailed ? <H3>Could not fetch data.</H3> 
              : (noData ? <H3>No data to display.</H3> : null) 
          }
        </Fragment>
      )}
    </View>
  )
}

const styles = {
  error: {
    border: `1px solid ${ theme.colors.error }`
  },
  selected: {
    backgroundColor: theme.colors.primaryLight
  },
  action: {
    paddingTop: 0,
    paddingBottom: 0,
    color: theme.colors.grey,
    fontSize: 18,
    width: 50
  },
  divider_no_padding: {
    padding_top: 0
  }
}

const getDividerClass = (index) => {
  return {
    color: theme.colors.grey,
    fontSize: 12,
    paddingBottom: 6,
    paddingTop: index ? 48 : 0,
  }
}

export default TableWidget